<template>
  <div class="tablero-clusters">
    <div class="tablero-cabecera">
      <div class="instruccional">
        <h2 class="titulo">Buscador de conglomerados</h2>
         <span class="fecha">{{obtenerFechaActualizacion}}</span>
      </div>
      
      <div class="instruccional">
        <p class="texto-instruccional">Los filtros seleccionan aquellos conglomerados con al menos un participante que cumpla las opciones elegidas.
          <span style="display:inline-block;position:relative;top:3px;">
            <BotonInfoTooltip 
              :contenido='"<p>Conglomerado</p><p>Un conglomerado es un conjunto de secuencias genéticas (de diferentes participantes) que comparten características en común.</p>"'
            />
          </span>
        </p>
      </div>
      <button type="button" class="boton-modal" @click="$refs.modal_buscador.abrirModal()">
            Acerca de la gráfica
        </button>
        <modal ref="modal_buscador" :posicion_boton="'arriba'">
            <template v-slot:cuerpo>
            <p>Las redes genéticas de transmisión de VIH permiten estudiar las características de conglomerados que corresponden a posibles cadenas de transmisión del virus y se definen comparando las distancias genéticas de los virus de todas las personas que componen la red. Cuando la distancia genética entre dos secuencias virales es menor a 1.5%, se establece un vínculo entre las dos personas, sugiriendo que éstas se encuentran en la misma cadena de transmisión. En la gráfica, cada círculo corresponde a una persona. Los círculos están coloreados por diferentes atributos, como la presencia de resistencia a fármacos antirretrovirales. Se muestran los conglomerados de la red, ordenados por número de integrantes. Con el cursor, es posible seleccionar conglomerados específicos para estudiar sus características particulares, como la edad, género, nivel de linfocitos T CD4+, carga viral, municipio/alcaldía de residencia, etc. </p>
            </template>
        </modal>
      <p class="numero-conglomerados-mov">{{numero_clusters}} conglomerados de acuerdo a la búsqueda</p>
    </div>
    <div class="tablero-cuerpo" >
      <Loader v-if="loaderVisible"/>
      <div class="item-check-resistencia">
        <div class="panel-porcentaje-muestra">
          <div class="porcentaje-descripcion">
            <p class="porcentaje-absoluto">
              <b>Personas que son parte de algún conglomerado:</b> 
              {{ Math.round((100 * numero_nodos_clusters) / nodos_totales) }}% ({{numero_nodos_clusters.toLocaleString("en")}})
            </p>
            <p class="porcentaje-total"><b>Muestra total:</b> {{nodos_totales.toLocaleString("en")}} personas </p>
          </div>
        </div>
        <div class="checks">
          <CheckResistencia class="contenedor-check"
            :titulo='"Resistencia"'
            :descripcion='"Tipo de antirretroviral al que presenta resistencia"'
            :opciones="opciones_antirretroviral"
            :variable_store="'check_antirretroviral'"
            :funcion_store="'cambiaAntirretroviral'"
          />
        </div>
      </div>
      <div class="item-graficas" :class="{'esta-abierto':filtros_abiertos, 'esta-abierto-movil':filtros_y_graficas_abiertos}">
        <div>
          <CheckBarras class="contenedor-check"
            :titulo='"Género"'
            :datos="data_clusters_sexo_d"
            :nombre_variables="{nombre:'nombre'}"
            :variables='[{"id":"cantidad","nombre":"nombre","color":"#333333"}]'
            :variable_store="'check_genero'"
            :funcion_store="'cambiaCheckGenero'"
          />
          <CheckBarras class="contenedor-check"
            :titulo='"Edad"'
            :datos="data_clusters_edad_d"
            :nombre_variables="{nombre:'nombre'}"
            :variables='[{"id":"cantidad", "nombre":"nombre", "color":"#333333"}]'
            :variable_store="'check_edad'"
            :funcion_store="'cambiaCheckEdad'"
          />
          <CheckBarras class="contenedor-check"
            :titulo='"Seguridad social"'
            :datos="data_clusters_seguro_social_d"
            :nombre_variables="{nombre:'nombre'}"
            :variables='[{"id":"cantidad", "nombre":"nombre", "color":"#333333"}]'
            :variable_store="'check_ss'"
            :funcion_store="'cambiaCheckSS'"
          />     
        </div>
      </div>
      <div class="item-visualizacion-principal">
        <SelectorOrden />
        <RedesClusters
          :datos="datos"
          :opciones_antirretroviral="opciones_antirretroviral"
          :cortes_carga_viral="cortes_carga_viral"
          :cortes_conteo_linfocitos="cortes_conteo_linfocitos"
          :cortes_distancia_genetica="cortes_distancia_genetica"
          :cortes_numero_pacientes="cortes_numero_pacientes"
        />
      </div>
      <div class="item-filtros">
        <div>
          <div class="tablero-filtros" :class="{'esta-abierto':filtros_abiertos, 'esta-abierto-movil':filtros_y_graficas_abiertos}">
            <CheckGeneral class="contenedor-check"
              :titulo='"Carga viral de VIH"'
              :descripcion='"Carga viral (copias de RNA/mL de plasma) en logaritmo"'
              :opciones="cortes_carga_viral"
              :variable_store="'check_carga_viral'"
              :funcion_store="'cambiaCheckCargaViral'"
              texto_info="<p>Carga Viral</p> <p>La carga viral es el número de virus circulando en la sangre de una persona con VIH.</p>"
            />
            <CheckGeneral class="contenedor-check"
              :titulo='"Conteo de linfocitos TCD4+"'
              :descripcion='"Conteo de células T CD4+ (células/mm<sup>3</sup> de sangre)"'
              :opciones="cortes_conteo_linfocitos"
              :variable_store="'check_conteo_linfocitos'"
              :funcion_store="'cambiaCheckConteoLinfocitos'"
              texto_info="<p>Conteo de linfocitos TCD4+</p> <p>Los linfocitos TCD4+ son un tipo de glóbulo blanco que ayuda a destruir las células tumorales y a controlar las respuestas inmunitarias.</p>"
            />
            <CheckGeneral class="contenedor-check"
              :titulo='"Distancia genética"'
              :descripcion='"Diferencia entre secuencias genéticas (%)"'
              :opciones="cortes_distancia_genetica"
              :variable_store="'check_distancia_genetica'"
              :funcion_store="'cambiaCheckDistanciaGenetica'"
              texto_info="<p>Distancia genética</p> <p>La distancia genética es la medida de la diferencia entre el material genético de dos o más organismos. Dicha diferencia varía de 0% a 100%, donde 0% significa que el material genético es igual y 100% significa que es completamente distinto.</p>"
            />
            <CheckGeneral class="contenedor-check"
              :titulo='"Participantes por conglomerado"'
              :descripcion='"Número de participantes que conforman un conglomerado"'
              :opciones="cortes_numero_pacientes"
              :variable_store="'check_pacientes_cluster'"
              :funcion_store="'cambiaCheckPacientesCluster'"
            />
          </div>
        </div>
      </div>
      <button class="btn btn-mas-menos-info"  type="button" @click="filtros_y_graficas_abiertos = !filtros_y_graficas_abiertos">
        <span>{{filtros_y_graficas_abiertos ? "Menos información" : "Más información"}}</span>
      </button>
    </div>
  </div>
</template>

<script>
import BotonInfoTooltip from "@/components/utils/BotonInfoTooltip.vue";
import RedesClusters from "@/components/tablero-clusters/graficas/RedesClusters.vue";
import SelectorOrden from "@/components/tablero-clusters/controles/SelectorOrden.vue";
import CheckGeneral from "@/components/tablero-clusters/controles/CheckGeneral.vue";
import CheckBarras from "@/components/tablero-clusters/controles/CheckBarras.vue";
import CheckResistencia from "@/components/tablero-clusters/controles/CheckResistencia.vue"
//import BarrasHorizontalesClusters from "@/components/tablero-clusters/graficas/BarrasHorizontalesClusters.vue"
//import BarrasVerticalesClusters from "@/components/tablero-clusters/graficas/BarrasVerticalesClusters.vue"
import Modal from '@/components/base/Modal.vue';

import Loader from '@/components/mapa/utiles/loader.vue'

import * as d3 from 'd3';
import { mapGetters, mapState } from 'vuex';

export default {
  name: 'TableroClustersProv',
  components: {
    RedesClusters,
    SelectorOrden,
    CheckGeneral,
    CheckResistencia,
    BotonInfoTooltip,
    Modal,
    //BarrasHorizontalesClusters,
    CheckBarras,
    //BarrasVerticalesClusters,
    Loader,
	},
  data() {
    return {
      data_clusters_seguro_social_d:[],
      data_clusters_sexo_d: [],
      data_clusters_edad_d: [],
      filtros_abiertos: true,
      filtros_y_graficas_abiertos: true,
      datos: {},
      nodos_totales: 1,
      opciones_antirretroviral: [
        {'id': 'cat-1', 'texto': 'Sin resistencia','valor': 'Sin resistencia','color': '#A726F5'},
        {'id': 'cat-2', 'texto': 'NNRTI','valor':'NNRTI','color': '#D62216'},
        {'id': 'cat-3', 'texto': 'Compleja','valor':'Compleja','color': '#445DE1'},
        {'id': 'cat-4', 'texto': 'NRTI','valor':'NRTI','color': '#E96C41'},
        {'id': 'cat-5', 'texto': 'INSTI','valor':'INSTI','color': '#703117'},
        {'id': 'cat-6', 'texto': 'PI','valor':'PI','color': '#DEB74D'}
      ],
      cortes_carga_viral: [
        {'id': 'cat-1', 'texto': '1 a 1.9','rango': [1,2]},
        {'id': 'cat-2', 'texto': '2 a 3.9','rango': [2,4]},
        {'id': 'cat-3', 'texto': '4 a 5.9','rango': [4,6]},
        {'id': 'cat-4', 'texto': '6 a 7','rango': [6,7.01]}
      ],
      cortes_conteo_linfocitos: [
        {'id': 'cat-1', 'texto': '0 a 99','rango': [0,100]},
        {'id': 'cat-2', 'texto': '100 a 199','rango': [100,200]},
        {'id': 'cat-3', 'texto': '200 a 499','rango': [200,500]},
        {'id': 'cat-4', 'texto': '500 a 1,500','rango': [500,1501]}
      ],
      cortes_distancia_genetica: [
        {'id': 'cat-1', 'texto': '0 a 0.5','rango': [0,0.5], "dash_array":"","stroke_color":"rgba(0, 0, 0, 1)","stroke_width":"1px"},
        {'id': 'cat-2', 'texto': '0.5 a 1','rango': [0.5,1], "dash_array":"","stroke_color":"rgba(180, 180, 180, 1)","stroke_width":"1px"},
        {'id': 'cat-3', 'texto': '1 a 1.5','rango': [1,1.5],"dash_array":"4 4", "stroke_color":"rgba(180, 180, 180, 1)","stroke_width":"1px"},
      ],
      cortes_numero_pacientes: [
        {'id': 'cat-1', 'texto': '2','rango': [2,3]},
        {'id': 'cat-2', 'texto': '3 a 4','rango': [3,5]},
        {'id': 'cat-3', 'texto': '5 a 7','rango': [5,8]},
        {'id': 'cat-4', 'texto': '8 a 16','rango': [8,100]}
      ],
      loaderVisible: true,
    };
  },
  mounted() {
    this.$store.state.modalCluster.abierto = false

    d3.json('data/tablero_red.json').then((data) => {
      data.nodes.forEach((d)=>{
        d.edad_rango = (d.edad_rango == "null" ? "Sin dato": 
          d.edad_rango == null ? "Sin dato": d.edad_rango 
        )
      })
      this.datos = data;
      this.nodos_totales = this.datos.nodes.length;
      this.$store.commit("cambiaNodosTotales",this.nodos_totales);//datos.nodes.length)

      this.$store.commit("cambiaCategoriasDistanciasGeneticas",this.cortes_distancia_genetica);
      var numero_maximo_nodos = d3.max(Array.from(
        d3.rollup(
          this.datos.nodes.filter(d => d.cluster_id != null && d.cluster_id != 0),
          (d) => ({num_nodos: d.length}),
          (d) => d.cluster_id
        )
      ).map((d) => d[1].num_nodos));

      this.cortes_numero_pacientes[3].texto = `8 a ${numero_maximo_nodos}`;
      this.cortes_numero_pacientes[3].rango = [8, numero_maximo_nodos + 1];
    });

    window.addEventListener("scroll", () => {
      if (this.loaderVisible && Object.keys(this.datos).includes("links")) {
        const alturaScroll = window.innerWidth < 768 ? 900 : 550;
        if (window.scrollY >= alturaScroll) {
          this.loaderVisible = false;
          this.ajustandoAltura()
        }
      }
    });
    
    window.addEventListener("resize", this.ajustandoAltura);

  },
  watch:{
    data_clusters_seguro_social(nv){
      this.data_clusters_seguro_social_d = nv;
    },
    data_clusters_sexo(nv){
      this.data_clusters_sexo_d = nv;
    },
    data_clusters_edad(nv){
      this.data_clusters_edad_d = nv;
    }
  },
  methods: {
    ajustandoAltura(){
      /*let altura_izq = document.querySelector("div.item-check-resistencia div.panel-porcentaje-muestra").clientHeight + 
        document.querySelector("div.item-check-resistencia div.check-contenido.contenedor-check").clientHeight;
      document.querySelector("div.item-check-resistencia").style.height = window.innerWidth > 769 ? altura_izq + "px" : "auto"
      
      let altura = document.querySelector("div.item-check-resistencia").clientHeight 
      + document.querySelector("div.item-filtros").clientHeight
      document.querySelector("div.item-graficas").style.height = window.innerWidth > 769 ? altura + "px" : "auto"*/
    },
  },
  computed: {
    ...mapState([
      'data_clusters_seguro_social', "data_clusters_sexo", "data_clusters_edad",
      "numero_clusters", "numero_nodos_clusters", "categorias_distancias_geneticas"
    ]),
    ...mapGetters(["obtenerFechaActualizacion"]),
  },
}
</script>

<style lang="scss">
label.checkbox {
  input {
    width: 30px!important;
    height: 30px!important;
  }
}
.check-titular {
  font-size: 16px;
  font-weight: 600;
  margin: 5px 0;
}
</style>

<style scoped lang="scss">
$border-color: #333;
$padding: $gap * .5;

.selector-orden {
  z-index: 2;
}

.tablero-clusters {
  border-radius: 10px 10px 0 0;
  border: 1px solid $border-color;
  font-size: 14px;
  background-color: white;
  padding-bottom: 10px;

  .tablero-cabecera {
    border-bottom: 1px solid $border-color;
    padding: $padding*.5 $padding;

    .titulo {
      font-size: 1.5em;
      line-height: 1.3;
      margin: 0;
    }
    .instruccional {
      margin: $padding*.5 0 0;
      display: flex;
      flex-direction: column;
      
      .texto-instruccional {
        margin: 0;
      }
      .fecha {
        display: block;
        margin-top: $padding*.5;
        font-size: 12px;
      }

      @media (min-width: map-get($media-queries-limit, "mobile")) {
        flex-direction: row;
        justify-content: space-between;
        margin: 0;
        position: relative;

        .texto-instruccional {
          margin: 0;
        }
        .fecha {
          margin: 0;
          position: absolute;
          right: 0;
          bottom: 0;
        }
      }
    }
    .numero-conglomerados-mov {
      padding-top: $padding*.5;
      color: #1996FC;
      font-size: 12px;
      font-weight: 600;
      margin: 0;

    }
  }

  .tablero-cuerpo {
    position: relative;
    transition: all .3s ease-in-out;
    display: grid;
    grid-template-columns: 100%;
    grid-template-rows: auto;
    grid-template-areas: 
      "resistencia"
      "vis-ppal"
      "filtros"
      "graficas";

    @media (min-width: map-get($media-queries-limit, "mobile")) {
      grid-template-columns: 248px calc(100% - 496px) 248px;
      
      grid-template-areas:
        "resistencia vis-ppal graficas"
        "resistencia vis-ppal graficas"
        "filtros filtros graficas";
    }

    .item-check-resistencia {
      grid-area: resistencia;
      max-width: 248px;

      @media (max-width: map-get($media-queries-limit, "mobile")) {
        border-right-width: 0;
        max-width: 100%;
      }

      .panel-porcentaje-muestra {
        padding: 5px 10px 5px 15px;
        border-bottom: 1px solid $border-color;
        display: flex;
        
        .porcentaje-descripcion {
          letter-spacing: -0.35px;
          font-size: 14px;

          .porcentaje-absoluto {
            color: #000;
            margin: 5px 0px;
          }
          .porcentaje-total {
            color: #6B6B6B;
            margin: 5px 0px;
          }
        }
      }
      .checks {
        .check-contenido {
          border-radius: 0;
          box-shadow: none;
          margin-bottom: 0;

          @media (max-width: map-get($media-queries-limit, "mobile")) {
            border-bottom: 1px solid $border-color;
          }
        }
      }
    }

    .item-graficas {
      grid-area: graficas;
      max-width: 100%;
      border-left: 1px solid $border-color;
      overflow-y: scroll;
      max-height: 604px;

      @media (max-width: map-get($media-queries-limit, "mobile")) {
        border-right-width: 0;
        border-left: none;
        position: relative;
        padding-bottom: 0;
        max-height: 0px;
        
        &.esta-abierto-movil {
         max-height: inherit;
        }
      }
    }
    .item-visualizacion-principal {
      grid-area: vis-ppal;
      padding: $padding;
      position: relative;
      
      @media (min-width: map-get($media-queries-limit, "mobile")) {
        border-left: 1px solid $border-color;
      }
    }
    .item-filtros {
      border-top: 1px solid $border-color;
      grid-area: filtros;

      .tablero-filtros {
        display: flex;
        width: 100%;
        overflow: hidden;
        flex-wrap: wrap;
        padding: 0;
        transition: all .3s ease-in-out;

        @media (max-width: map-get($media-queries-limit, "mobile")) {
          padding-top: $gap*.25;
          display: block;
          max-height: 0px;

          &.esta-abierto-movil {
            max-height: inherit;
          }
        }

        .contenedor-check:not(:last-of-type) {
          position: relative;
          padding-right: $padding;

          &::after {
            content: '';
            background: #000;
            display: block;
            width: 1px;
            position: absolute;
            right: $padding*.5;
            top: 0;
            bottom: -$padding*.5;
          }
        }
        
        @media (max-width: map-get($media-queries-limit, "mobile")) {
          .contenedor-check:not(:last-of-type), .contenedor-check {
            position: relative;
            margin-bottom: 20px;
            padding-right: $padding;

            &::after {
              content: '';
              background: #000;
              display: block;
              width: 100%;
              height: 1px;
              position: absolute;
              left: 0;
              top: calc(100% + 5px);
              bottom: $padding*.5;
            }
          }
        }
      }
    }
    .btn-mas-menos-info {
      display: none;

      @media (max-width: map-get($media-queries-limit, "mobile")) {
        display: block;
        width: 100%;
        border-radius: 0 0 0 0;
      }
    }
  }
}
</style>
